import * as React from 'react';

import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';

import { cn } from '@/lib/utils';

interface ScrollAreaProps extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  showShadow?: boolean;
  viewportClassName?: string;
  /** Forwards the inner scrolling viewport element. Use it as the `root` for an IntersectionObserver
   *  (e.g. `useInView`) so observers fire on inner scrolls instead of the document viewport. */
  viewportRef?: React.Ref<HTMLDivElement>;
  /** When true, allows content to have natural height instead of forcing h-full on children */
  autoHeight?: boolean;
}

const ScrollArea = React.forwardRef<React.ElementRef<typeof ScrollAreaPrimitive.Root>, ScrollAreaProps>(
  ({ className, children, showShadow = false, viewportClassName, viewportRef: externalViewportRef, autoHeight = false, ...props }, ref) => {
    // `<HTMLDivElement | null>` (not `<HTMLDivElement>`) so useRef resolves to MutableRefObject — needed because we write to .current from setViewport below.
    const viewportRef = React.useRef<HTMLDivElement | null>(null);
    const setViewport = React.useCallback(
      (node: HTMLDivElement | null) => {
        viewportRef.current = node;
        if (typeof externalViewportRef === 'function') {
          externalViewportRef(node);
        } else if (externalViewportRef) {
          // React.Ref narrows to RefObject in this branch; the `as` cast bypasses RefObject.current's readonly type so we can write to it (the canonical merged-ref pattern).
          (externalViewportRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [externalViewportRef],
    );
    const [showTop, setShowTop] = React.useState(false);
    const [showBottom, setShowBottom] = React.useState(false);
    const [showLeft, setShowLeft] = React.useState(false);
    const [showRight, setShowRight] = React.useState(false);

    React.useEffect(() => {
      if (!showShadow) return;
      const el = viewportRef.current;
      if (!el) return;

      const checkOverflow = () => {
        setShowTop(el.scrollTop > 0);
        setShowBottom(el.scrollTop + el.clientHeight < el.scrollHeight);
        setShowLeft(el.scrollLeft > 0);
        setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
      };

      checkOverflow();
      el.addEventListener('scroll', checkOverflow, { passive: true });

      // Use ResizeObserver to detect content size changes (e.g., when "Preview more" adds content)
      const resizeObserver = new ResizeObserver(() => {
        checkOverflow();
      });

      // Observe both the viewport and its first child (the content)
      resizeObserver.observe(el);
      if (el.firstElementChild) {
        resizeObserver.observe(el.firstElementChild);
      }

      return () => {
        el.removeEventListener('scroll', checkOverflow);
        resizeObserver.disconnect();
      };
    }, [showShadow]);

    return (
      <ScrollAreaPrimitive.Root ref={ref} className={cn('relative overflow-hidden', className)} {...props}>
        {showShadow && (
          <>
            {/* Top shadow */}
            <div
              className={cn(
                'pointer-events-none absolute top-0 right-0 left-0 z-10 h-4 bg-gradient-to-b from-black/10 to-transparent transition-opacity duration-300',
                showTop ? 'opacity-100' : 'opacity-0',
              )}
            />
            {/* Bottom shadow */}
            <div
              className={cn(
                'pointer-events-none absolute right-0 bottom-0 left-0 z-10 h-4 bg-gradient-to-t from-black/10 to-transparent transition-opacity duration-300',
                showBottom ? 'opacity-100' : 'opacity-0',
              )}
            />
            {/* Left shadow */}
            <div
              className={cn(
                'pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-4 bg-gradient-to-r from-black/10 to-transparent transition-opacity duration-300',
                showLeft ? 'opacity-100' : 'opacity-0',
              )}
            />
            {/* Right shadow */}
            <div
              className={cn(
                'pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-4 bg-gradient-to-l from-black/10 to-transparent transition-opacity duration-300',
                showRight ? 'opacity-100' : 'opacity-0',
              )}
            />
          </>
        )}

        <ScrollAreaPrimitive.Viewport ref={setViewport} className={cn('h-full w-full rounded-[inherit]', !autoHeight && '[&>*]:h-full', viewportClassName)}>
          {children}
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar />
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    );
  },
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'z-50 flex touch-none transition-colors select-none',
      orientation === 'vertical' && 'h-full w-2.5 border-l border-l-transparent p-[1px]',
      orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent p-[1px]',
      className,
    )}
    {...props}>
    <ScrollAreaPrimitive.ScrollAreaThumb className="bg-grey-5 relative flex-1 rounded-full" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
